import { useEffect, useMemo, useSyncExternalStore } from "react";

import { createAnnotationStudioController } from "./AnnotationStudioController";
import type {
  AnnotationStudioController,
  AnnotationStudioControllerOptions,
  AnnotationStudioControllerSnapshot,
} from "./types";

export function useAnnotationStudioController(
  options: AnnotationStudioControllerOptions,
): AnnotationStudioController {
  const authoring = options.mode === "authoring" ? options.authoring : undefined;
  const controller = useMemo(
    () => createAnnotationStudioController(options),
    // locale/labels are presentation values and update the same controller below. Source/driver/mode
    // and one-time hydration are identity-bearing and intentionally create a new controller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      options.workspaceSource,
      options.sequenceDirectory,
      options.savedFrameIndex,
      options.frameMaterializer,
      options.initialSession,
      options.mode,
      authoring,
      options.resolveMediaSource,
      options.segmentation,
      options.annotationSetValidation,
      options.trackingPreview,
      options.exitPreflightOrder,
    ],
  );

  useEffect(() => {
    controller.updatePresentation(options.locale, options.labels);
  }, [controller, options.labels, options.locale]);

  useEffect(() => controller.connect(), [controller]);

  return controller;
}

export function useAnnotationStudioSnapshot(
  controller: AnnotationStudioController,
): AnnotationStudioControllerSnapshot {
  return useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
}
